import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, LogIn, UserCircle2, HeartHandshake } from "lucide-react";
import { ASSETS, GURUKULA } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const NAV_LINKS = [
    { to: "/#about", label: "About" },
    { to: "/#founder", label: "Founder" },
    { to: "/#courses", label: "Courses" },
    { to: "/vision", label: "Vision" },
    { to: "/social-service", label: "Social" },
    { to: "/#contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const authed = user && typeof user === "object";

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-50 bg-ivory/85 backdrop-blur-xl border-b border-gold-100"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between gap-8">
                <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 shrink-0">
                    <img
                        src={ASSETS.logo}
                        alt="Shri Vishwa Bandhu Sangeetha Gurukula"
                        className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/40"
                    />
                    <div className="leading-tight whitespace-nowrap">
                        <div className="font-display text-[15px] sm:text-[17px] text-maroon font-semibold tracking-wide">
                            Shri Vishwa Bandhu
                        </div>
                        <div className="eyebrow text-[10px] sm:text-[11px] text-gold mt-0.5">
                            Sangeetha Gurukula
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) =>
                        l.to.startsWith("/#") ? (
                            <a
                                key={l.label}
                                href={l.to}
                                data-testid={`nav-${l.label.toLowerCase()}-link`}
                                className="relative text-[13px] font-sans tracking-[0.22em] uppercase text-ink hover:text-maroon transition-colors py-1"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                key={l.label}
                                to={l.to}
                                data-testid={`nav-${l.label.toLowerCase()}-link`}
                                className="relative text-[13px] font-sans tracking-[0.22em] uppercase text-ink hover:text-maroon transition-colors py-1"
                            >
                                {l.label}
                                {l.label === "Vision" && (
                                    <span
                                        className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[10px] tracking-[0.32em] font-sans font-semibold text-gold whitespace-nowrap"
                                        data-testid="nav-vision-sub"
                                    >
                                        · NADAMANDIR ·
                                    </span>
                                )}
                            </Link>
                        )
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3 shrink-0">
                    <Link
                        to="/vision#donate"
                        data-testid="navbar-donate-button"
                        className="inline-flex items-center gap-2 bg-gold text-maroon font-sans text-[11px] tracking-[0.26em] uppercase px-5 py-3 border border-gold hover:bg-gold-300 transition-all hover:shadow-[0_4px_20px_-4px_rgba(181,138,67,0.6)]"
                    >
                        <HeartHandshake className="w-4 h-4" />
                        Donate
                    </Link>
                    {authed ? (
                        <NavLink
                            to="/dashboard"
                            data-testid="navbar-dashboard-button"
                            className="btn-primary"
                        >
                            <UserCircle2 className="inline-block w-4 h-4 mr-2 -mt-0.5" />
                            My Gurukula
                        </NavLink>
                    ) : (
                        <>
                            <Link
                                to="/register"
                                data-testid="navbar-enroll-button"
                                className="btn-primary"
                            >
                                Enroll
                            </Link>
                            <Link
                                to="/login"
                                data-testid="navbar-login-button"
                                className="inline-flex items-center gap-1 text-[10px] font-sans tracking-[0.22em] uppercase text-maroon border border-gold/60 px-3 py-1.5 hover:bg-maroon hover:text-ivory transition-colors"
                                title="Student Login"
                            >
                                <LogIn className="w-3 h-3" />
                                Login
                            </Link>
                        </>
                    )}
                </div>

                <button
                    data-testid="navbar-mobile-toggle"
                    className="lg:hidden p-2 text-maroon"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle navigation"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-gold-100 bg-ivory">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) =>
                            l.to.startsWith("/#") ? (
                                <a
                                    key={l.label}
                                    href={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mnav-${l.label.toLowerCase()}-link`}
                                    className="text-[14px] tracking-[0.18em] uppercase text-ink"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mnav-${l.label.toLowerCase()}-link`}
                                    className="text-[14px] tracking-[0.18em] uppercase text-ink"
                                >
                                    {l.label}
                                </Link>
                            )
                        )}
                        {authed ? (
                            <Link to="/dashboard" className="btn-primary text-center" data-testid="mnav-dashboard-btn">
                                My Gurukula
                            </Link>
                        ) : (
                            <div className="flex gap-3">
                                <Link to="/login" className="btn-ghost-gold flex-1 text-center" data-testid="mnav-login-btn">
                                    Login
                                </Link>
                                <Link to="/register" className="btn-primary flex-1 text-center" data-testid="mnav-enroll-btn">
                                    Enroll
                                </Link>
                            </div>
                        )}
                        <Link
                            to="/vision#donate"
                            onClick={() => setOpen(false)}
                            data-testid="mnav-donate-btn"
                            className="inline-flex items-center justify-center gap-2 bg-gold text-maroon font-sans text-[11px] tracking-[0.26em] uppercase px-5 py-3 hover:bg-gold-300 transition-all"
                        >
                            <HeartHandshake className="w-4 h-4" />
                            Donate
                        </Link>
                        <a
                            href={`tel:${GURUKULA.phone}`}
                            className="text-maroon font-sans font-medium text-2xl tabular-nums tracking-[0.1em] mt-3"
                        >
                            Call · {GURUKULA.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
